// 専用 配合比マスタ画面 (モックアップ rev2.9〜 を Web へ移植)。
// 配合比定義の一覧をアコーディオン表示し、各定義の明細を展開内で CRUD する。

import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Stack, Typography, Paper, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Table, TableHead, TableBody, TableRow, TableCell,
  Accordion, AccordionDetails, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import type { ExtendedDB } from '../../../types/extendedDB';
import { useFullDb, replaceCollection, replaceCollections, nextIdFor } from '../../../api/dbSlice';
import { MasterAccordionSummary } from './MasterAccordionSummary';
import { useIsOnline } from '../../../util/useIsOnline';
import { OfflineMasterBanner } from '../../../components/OfflineMasterBanner';
import { usePartitionFilter } from '../../../util/partitionFilterStore';
import { mixRatioDisplayName } from '../../../util/mixRatioLabel';
import type { MixRatioDefinitionBase } from '../../../schema/generated/MixRatioDefinition';
import type { MixRatioDetailBase } from '../../../schema/generated/MixRatioDetail';
import { PageContext } from '../PageContext';
import { WORD } from '../../../word';
import { renderWord } from '../../../util/renderWord';
import Time from '../../../util/Time';
import { EmptyState } from '../../../components/EmptyState';
import { errorMessageOf } from "../../../util/typed";
import { ContentLoading } from "../../../components/Loading";

type MixRatioDefinition = MixRatioDefinitionBase;
type MixRatioDetail = MixRatioDetailBase;

function formatRatioRangeCompact(ratioStart: number, ratioEnd: number | null): string {
  if (ratioEnd != null) {
    return `${ratioStart}〜${ratioEnd}`;
  }
  return `${ratioStart}`;
}

// ---- 明細編集ダイアログ ----
// 構成細目は所属する定義の material_id 配下のみ選択可能 (参照整合性の確保)。
function MixRatioDetailEditDialog({ open, value, db, parentDef, isOnline, onClose, onSave }: {
  open: boolean;
  value: MixRatioDetail | null;
  db: ExtendedDB;
  parentDef: MixRatioDefinition | null;
  isOnline: boolean;
  onClose: () => void;
  onSave: (next: MixRatioDetail) => void;
}) {
  const [draft, setDraft] = useState<MixRatioDetail | null>(null);
  useEffect(() => { setDraft(value ? { ...value } : null); }, [value]);

  // 定義の material_id に紐付く細目だけを選択肢に出す。
  const detailOptions = useMemo(() => {
    const parentMaterialId = parentDef?.material_id;
    return (db.materialDetails ?? [])
      .filter((d) => d.deleted_at == null && (parentMaterialId == null || d.material_id === parentMaterialId))
      .map((d) => {
        const m = db.materials.find((x) => x.id === d.material_id);
        return { value: d.id, label: `${m?.name ?? '-'} / ${d.detail_name}` };
      });
  }, [db.materialDetails, db.materials, parentDef]);

  if (!draft) return null;
  const update = <K extends keyof MixRatioDetail>(k: K, v: MixRatioDetail[K]) => setDraft({ ...draft, [k]: v });

  const findLinkedDetail = () => {
    if (draft.material_detail_id != null) {
      return db.materialDetails.find((x) => x.id === draft.material_detail_id);
    }
    return undefined;
  };
  const linkedDetail = findLinkedDetail();
  const derivedUnit = linkedDetail?.content_unit ?? 'kg';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>配合比明細の編集</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <TextField
            select label="構成細目" size="small" fullWidth required
            value={draft.material_detail_id ?? ''}
            onChange={(e) => update('material_detail_id', Number(e.target.value))}
            helperText={WORD.mixRatio.hDetailMaterialDetail}
          >
            {detailOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <Stack direction="row" gap={2}>
            <TextField
              label="配合比 開始" size="small" type="number" required
              value={draft.ratio_start ?? ''} onChange={(e) => update('ratio_start', e.target.value === '' ? 0 : Number(e.target.value))}
            />
            <TextField
              label="配合比 終了" size="small" type="number"
              value={draft.ratio_end ?? ''}
              onChange={(e) => update('ratio_end', e.target.value === '' ? null : Number(e.target.value))}
              helperText={WORD.mixRatio.hRatioEnd}
            />
            <TextField
              label="単位（自動）" size="small" disabled
              value={derivedUnit}
              helperText={WORD.mixRatio.hDerivedUnit}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Button variant="contained" disabled={!isOnline || !draft.material_detail_id} onClick={() => onSave(draft)}>保存</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---- 定義編集ダイアログ ----
// 材料セレクタは定義の material_division_id に紐付く材料のみ選択可能 (参照整合性の確保)。
function MixRatioDefinitionEditDialog({ open, value, isNew, db, isOnline, onClose, onSave }: {
  open: boolean;
  value: MixRatioDefinition | null;
  isNew: boolean;
  db: ExtendedDB;
  isOnline: boolean;
  onClose: () => void;
  onSave: (next: MixRatioDefinition) => void;
}) {
  const [draft, setDraft] = useState<MixRatioDefinition | null>(null);
  useEffect(() => {
    setDraft(value ? { ...value } : null);
  }, [value]);
  if (!draft) return null;
  const d = draft;
  const update = <K extends keyof MixRatioDefinition>(k: K, v: MixRatioDefinition[K]) => setDraft({ ...d, [k]: v });
  const materialOptions = db.materials.filter((m) => m.deleted_at == null && m.material_division_id === d.material_division_id);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isNew ? '配合比を追加' : '配合比を編集'}</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <TextField
            select label="材料" size="small" fullWidth required
            value={materialOptions.some((m) => m.id === d.material_id) ? d.material_id : ''}
            onChange={(e) => update('material_id', Number(e.target.value))}
            helperText={WORD.mixRatio.hMaterial}
          >
            {materialOptions.map((m) => (
              <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select label="施工方法" size="small"
            value={draft.construction_method_id ?? ''}
            onChange={(e) => update('construction_method_id', e.target.value === '' ? null : Number(e.target.value))}
            helperText={WORD.mixRatio.hConstructionMethod}
          >
            <MenuItem value=""><em>(未設定)</em></MenuItem>
            {db.constructionMethods.filter((c) => c.deleted_at == null).map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select label="撹拌方法" size="small"
            value={draft.stirring_method_id ?? ''}
            onChange={(e) => update('stirring_method_id', e.target.value === '' ? null : Number(e.target.value))}
            helperText={WORD.mixRatio.hStirringMethod}
          >
            <MenuItem value=""><em>(未設定)</em></MenuItem>
            {db.stirringMethods.filter((s) => s.deleted_at == null).map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="撹拌時間（分）" size="small" type="number" required
            value={draft.stirring_time_minutes ?? 2}
            onChange={(e) => update('stirring_time_minutes', Number(e.target.value))}
          />
          <TextField
            label="備考" size="small" fullWidth multiline minRows={2}
            value={draft.remarks ?? ''}
            onChange={(e) => update('remarks', e.target.value === '' ? null : e.target.value)}
            helperText={WORD.mixRatio.hRemarks}
          />
          {isNew && (
            <Alert severity="info" sx={{ py: 0.5 }}>{renderWord(WORD.mixRatio.defDialogInfo)}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Button variant="contained" disabled={!isOnline || !draft.material_id} onClick={() => onSave(draft)}>保存</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---- メインページ ----
export default function MixRatioMasterPage({ pageContext, showOfflineBanner = true }: { pageContext: PageContext; showOfflineBanner?: boolean }) {
  const idb = pageContext.api.indexedDB;
  const ownerId = pageContext.api.currentUser.id;
  const { db, loading } = useFullDb(idb, ownerId);
  const isOnline = useIsOnline(pageContext.api);

  const allDefinitions = (db.mixRatioDefinitions ?? []).filter((d) => d.deleted_at == null);
  const allDetails = (db.mixRatioDetails ?? []).filter((d) => d.deleted_at == null);

  const partitionOptions = useMemo(() => {
    return db.materialDivisions
      .filter((d) => d.is_active && d.deleted_at == null)
      .sort((a, b) => a.display_order - b.display_order)
      .map((d) => ({ value: d.id, label: `#${d.id} ${d.name}` }));
  }, [db.materialDivisions]);
  const [filterValue, setFilterValue] = usePartitionFilter('material_division_id');
  useEffect(() => {
    if (partitionOptions.length === 0) return;
    if (filterValue === null || !partitionOptions.some((o) => o.value === filterValue)) {
      const firstOption = partitionOptions[0];
      if (firstOption !== undefined) {
        setFilterValue(firstOption.value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partitionOptions]);

  const visibleDefinitions = useMemo(
    () => {
      if (filterValue == null) {
        return allDefinitions;
      }
      return allDefinitions.filter((d) => d.material_division_id === filterValue);
    },
    [allDefinitions, filterValue]
  );

  // ---- 編集ステート ----
  const [editingDef, setEditingDef] = useState<MixRatioDefinition | null>(null);
  const [editingDefIsNew, setEditingDefIsNew] = useState(false);
  const [confirmDeleteDef, setConfirmDeleteDef] = useState<MixRatioDefinition | null>(null);
  const [editingDetail, setEditingDetail] = useState<MixRatioDetail | null>(null);
  const [editingDetailParent, setEditingDetailParent] = useState<MixRatioDefinition | null>(null);
  const [confirmDeleteDetail, setConfirmDeleteDetail] = useState<MixRatioDetail | null>(null);

  // ---- 配合比定義 CRUD ----
  const openNewDef = async () => {
    const newId = await nextIdFor(idb, ownerId, 'mixRatioDefinitions');
    const now = Time.Now().GetTime();
    const initial: MixRatioDefinition = {
      id: newId,
      createdAt: now,
      updatedAt: now,
      deleted_at: null,
      material_division_id: filterValue ?? 1,
      material_id: db.materials[0]?.id ?? 0,
      stirring_time_minutes: 2,
      stirring_method_id: null,
      construction_method_id: null,
      remarks: null,
    };
    setEditingDef(initial);
    setEditingDefIsNew(true);
  };
  const openEditDef = (d: MixRatioDefinition) => { setEditingDef({ ...d }); setEditingDefIsNew(false); };

  const saveDefinition = async (next: MixRatioDefinition) => {
    try {
      const now = Time.Now().GetTime();
      const nextWithTs: MixRatioDefinition = { ...next, updatedAt: now };
      const curDefs = (db.mixRatioDefinitions ?? []);
      const nextDefs = curDefs.some((x) => x.id === next.id)
        ? curDefs.map((x) => x.id === next.id ? nextWithTs : x)
        : [...curDefs, nextWithTs];
      await replaceCollection(idb, ownerId, 'mixRatioDefinitions', nextDefs, pageContext.api);
      setEditingDef(null);
      setEditingDefIsNew(false);
    } catch (e) {
      pageContext.api.showError(`配合比の保存に失敗しました。\n${errorMessageOf(e)}`);
    }
  };

  const duplicateDefinition = async (def: MixRatioDefinition) => {
    try {
      const now = Time.Now().GetTime();
      const curDefs = (db.mixRatioDefinitions ?? []);
      const curDetails = (db.mixRatioDetails ?? []);
      const newDefId = await nextIdFor(idb, ownerId, 'mixRatioDefinitions');
      const newDef: MixRatioDefinition = { ...def, id: newDefId, createdAt: now, updatedAt: now, deleted_at: null };
      const nextDefs = [...curDefs, newDef];

      const origDetails = curDetails.filter((d) => d.mix_ratio_definition_id === def.id && d.deleted_at == null);
      let detailNextId = await nextIdFor(idb, ownerId, 'mixRatioDetails');
      const newDetails: MixRatioDetail[] = origDetails.map((d) => {
        const copy: MixRatioDetail = { ...d, id: detailNextId, mix_ratio_definition_id: newDefId, createdAt: now, updatedAt: now, deleted_at: null };
        detailNextId += 1;
        return copy;
      });
      const nextDetails = [...curDetails, ...newDetails];
      await replaceCollections(idb, ownerId, { mixRatioDefinitions: nextDefs, mixRatioDetails: nextDetails }, pageContext.api);
    } catch (e) {
      pageContext.api.showError(`配合比の複製に失敗しました。\n${errorMessageOf(e)}`);
    }
  };

  const deleteDefinition = async (def: MixRatioDefinition) => {
    try {
      const now = Time.Now().GetTime();
      const nextDefs = ((db.mixRatioDefinitions ?? []))
        .map((x) => x.id === def.id ? { ...x, deleted_at: now, updatedAt: now } : x);
      const nextDetails = ((db.mixRatioDetails ?? []))
        .map((d) => d.mix_ratio_definition_id === def.id ? { ...d, deleted_at: now, updatedAt: now } : d);
      await replaceCollections(idb, ownerId, { mixRatioDefinitions: nextDefs, mixRatioDetails: nextDetails }, pageContext.api);
      setConfirmDeleteDef(null);
    } catch (e) {
      pageContext.api.showError(`配合比の削除に失敗しました。\n${errorMessageOf(e)}`);
    }
  };

  // ---- 配合比明細 CRUD ----
  const openNewDetail = async (def: MixRatioDefinition) => {
    const newId = await nextIdFor(idb, ownerId, 'mixRatioDetails');
    const now = Time.Now().GetTime();
    // 親定義の material_id 配下の細目を初期値に。なければ空 (UI 側で選択必須)。
    const defaultDetail = db.materialDetails.find((d) => d.deleted_at == null && d.material_id === def.material_id);
    const initial: MixRatioDetail = {
      id: newId,
      createdAt: now,
      updatedAt: now,
      deleted_at: null,
      material_division_id: def.material_division_id,
      material_id: def.material_id,
      mix_ratio_definition_id: def.id,
      material_detail_id: defaultDetail?.id ?? 0,
      ratio_start: 1,
      ratio_end: null,
    };
    setEditingDetailParent(def);
    setEditingDetail(initial);
  };
  const openEditDetail = (d: MixRatioDetail) => {
    const parent = allDefinitions.find((def) => def.id === d.mix_ratio_definition_id) ?? null;
    setEditingDetailParent(parent);
    setEditingDetail({ ...d });
  };

  // 選択された material_detail_id から denorm な material_id / material_division_id を再導出。
  // 親定義との不整合 (material_id 不一致) はガードする。
  const deriveDenormFromDetail = (next: MixRatioDetail): MixRatioDetail | { __error: string } => {
    const linkedDetail = db.materialDetails.find((x) => x.id === next.material_detail_id);
    if (!linkedDetail) {
      return { __error: '構成細目が見つかりません。' };
    }
    if (editingDetailParent && linkedDetail.material_id !== editingDetailParent.material_id) {
      return { __error: '構成細目の材料が配合比定義の材料と一致しません。' };
    }
    return { ...next, material_id: linkedDetail.material_id, material_division_id: linkedDetail.material_division_id };
  };

  const saveDetail = async (raw: MixRatioDetail) => {
    try {
      const now = Time.Now().GetTime();
      const nextOrError = deriveDenormFromDetail(raw);
      if ('__error' in nextOrError) {
        pageContext.api.showError(nextOrError.__error);
        return;
      }
      const next = nextOrError;
      const nextWithTs: MixRatioDetail = { ...next, updatedAt: now };
      const cur = (db.mixRatioDetails ?? []);
      const nextDetails = cur.some((d) => d.id === next.id)
        ? cur.map((d) => d.id === next.id ? nextWithTs : d)
        : [...cur, nextWithTs];
      await replaceCollection(idb, ownerId, 'mixRatioDetails', nextDetails, pageContext.api);
      setEditingDetail(null);
        setEditingDetailParent(null);
    } catch (e) {
      pageContext.api.showError(`配合比明細の保存に失敗しました。\n${errorMessageOf(e)}`);
    }
  };

  const deleteDetail = async (d: MixRatioDetail) => {
    try {
      const now = Time.Now().GetTime();
      const nextDetails = ((db.mixRatioDetails ?? []))
        .map((x) => x.id === d.id ? { ...x, deleted_at: now, updatedAt: now } : x);
      await replaceCollection(idb, ownerId, 'mixRatioDetails', nextDetails, pageContext.api);
      setConfirmDeleteDetail(null);
    } catch (e) {
      pageContext.api.showError(`配合比明細の削除に失敗しました。\n${errorMessageOf(e)}`);
    }
  };

  const duplicateDetail = async (d: MixRatioDetail) => {
    try {
      const now = Time.Now().GetTime();
      const cur = (db.mixRatioDetails ?? []);
      const newId = await nextIdFor(idb, ownerId, 'mixRatioDetails');
      const copy: MixRatioDetail = { ...d, id: newId, createdAt: now, updatedAt: now, deleted_at: null };
      await replaceCollection(idb, ownerId, 'mixRatioDetails', [...cur, copy], pageContext.api);
    } catch (e) {
      pageContext.api.showError(`配合比明細の複製に失敗しました。\n${errorMessageOf(e)}`);
    }
  };

  // ---- アコーディオン状態 ----
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const handleExpand = (id: number) => setExpandedId((prev) => prev === id ? null : id);

  const matName = (matId: number) => db.materials.find((m) => m.id === matId)?.name ?? '-';
  const detailLabel = (detailId: number) => {
    const d = db.materialDetails.find((x) => x.id === detailId);
    if (d == null) {
      return '-';
    }
    const matId = d.material_id;
    const name = d.detail_name;
    const m = db.materials.find((mm) => mm.id === matId);
    return `${m?.name ?? '-'} / ${name}`;
  };

  if (loading) {
    return <ContentLoading />;
  }

  return (
    // 余白と最大幅は親のマスター編集画面が持つため、ここでは内側の並びだけを指定する。
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Typography variant="body2" color="text.secondary">材料区分 で絞り込み:</Typography>
        <TextField
          select size="small" value={filterValue ?? ''}
          onChange={(e) => setFilterValue(e.target.value === '' ? null : Number(e.target.value))}
          sx={{ minWidth: 220 }}
        >
          {partitionOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <Typography variant="caption" color="text.secondary">
          （{visibleDefinitions.length} / 全 {allDefinitions.length} 件）
        </Typography>
        <Box flex={1} />
        <Button variant="contained" startIcon={<AddIcon />} disabled={!isOnline} onClick={openNewDef}>新規追加</Button>
      </Stack>

      {showOfflineBanner && <OfflineMasterBanner isOnline={isOnline} />}

      <Alert severity="info" sx={{ py: 0.5 }}>{renderWord(WORD.mixRatio.listInfo)}</Alert>

      <Paper variant="outlined">
        <Box sx={{ overflowX: 'auto' }}>
          {visibleDefinitions.length === 0 && (
            <EmptyState message='該当する配合比定義がありません。「新規追加」から登録してください。' py={4} />
          )}
          {visibleDefinitions.map((def) => {
            const details = allDetails
              .filter((d) => d.mix_ratio_definition_id === def.id)
              .sort((a, b) => a.id - b.id);
            const recipeLabel = mixRatioDisplayName(db, def);
            return (
              <Accordion key={def.id} disableGutters expanded={expandedId === def.id} onChange={() => handleExpand(def.id)}>
                <MasterAccordionSummary
                  idLabel={`#${def.id}`}
                  title={<Typography variant="body2" fontWeight={600} sx={{ minWidth: 160 }}>{matName(def.material_id)}</Typography>}
                  chips={<>
                    <Typography variant="body2" sx={{ minWidth: 240 }} color="text.secondary">{recipeLabel}</Typography>
                    {def.construction_method_id != null && (
                      <Chip size="small" color="primary" variant="outlined"
                        label={db.constructionMethods.find((c) => c.id === def.construction_method_id)?.name ?? '?'} />
                    )}
                    {def.stirring_method_id != null && (
                      <Chip size="small" color="secondary" variant="outlined"
                        label={db.stirringMethods.find((s) => s.id === def.stirring_method_id)?.name ?? '?'} />
                    )}
                    <Chip size="small" variant="outlined" label={`撹拌 ${def.stirring_time_minutes}分`} />
                    <Box flex={1} />
                    <Stack direction="row" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
                      {details.slice(0, 4).map((d) => {
                        const md = db.materialDetails.find((x) => x.id === d.material_detail_id);
                        const range = formatRatioRangeCompact(d.ratio_start, d.ratio_end);
                        return (
                          <Chip key={d.id} size="small" color="info" variant="outlined"
                            label={`${md?.detail_name ?? '-'}=${range}`} />
                        );
                      })}
                      {details.length > 4 && <Chip size="small" label={`+${details.length - 4}`} />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {details.length} 明細
                    </Typography>
                  </>}
                  actions={<>
                    <IconButton size="small" title="編集" disabled={!isOnline} onClick={() => openEditDef(def)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" title="複製" disabled={!isOnline} onClick={() => duplicateDefinition(def)}><ContentCopyIcon fontSize="small" /></IconButton>
                    <IconButton size="small" title="削除" disabled={!isOnline} onClick={() => setConfirmDeleteDef(def)}><DeleteIcon fontSize="small" /></IconButton>
                  </>}
                />
                <AccordionDetails sx={{ backgroundColor: 'grey.50' }}>
                  <Stack gap={1.5}>
                    {def.remarks && (
                      <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'background.paper' }}>
                        <Typography variant="caption" color="text.secondary">備考</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{def.remarks}</Typography>
                      </Paper>
                    )}
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="subtitle2">配合比明細 ({details.length} 件)</Typography>
                      <Box flex={1} />
                      <Button size="small" startIcon={<AddIcon />} disabled={!isOnline} onClick={() => openNewDetail(def)}>明細を追加</Button>
                    </Stack>
                    <Paper variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', width: 60 }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>構成細目（材料 / 細目）</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: 140 }}>配合比範囲</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: 80 }}>単位</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: 130 }}>操作</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {details.map((d) => {
                            const md = db.materialDetails.find((x) => x.id === d.material_detail_id);
                            const unitDerived = md?.content_unit ?? '-';
                            return (
                              <TableRow key={d.id}>
                                <TableCell>{d.id}</TableCell>
                                <TableCell>{detailLabel(d.material_detail_id)}</TableCell>
                                <TableCell>{d.ratio_end != null ? `${d.ratio_start} 〜 ${d.ratio_end}` : `${d.ratio_start}`}</TableCell>
                                <TableCell>{unitDerived}</TableCell>
                                <TableCell>
                                  <IconButton size="small" title="編集" disabled={!isOnline} onClick={() => openEditDetail(d)}><EditIcon fontSize="small" /></IconButton>
                                  <IconButton size="small" title="複製" disabled={!isOnline} onClick={() => duplicateDetail(d)}><ContentCopyIcon fontSize="small" /></IconButton>
                                  <IconButton size="small" title="削除" disabled={!isOnline} onClick={() => setConfirmDeleteDetail(d)}><DeleteIcon fontSize="small" /></IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {details.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                明細未登録。「明細を追加」から登録してください。
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Paper>

      <MixRatioDefinitionEditDialog
        open={!!editingDef}
        value={editingDef}
        isNew={editingDefIsNew}
        db={db}
        isOnline={isOnline}
        onClose={() => { setEditingDef(null); setEditingDefIsNew(false); }}
        onSave={saveDefinition}
      />

      <MixRatioDetailEditDialog
        open={!!editingDetail}
        value={editingDetail}
        db={db}
        parentDef={editingDetailParent}
        isOnline={isOnline}
        onClose={() => { setEditingDetail(null); setEditingDetailParent(null); }}
        onSave={saveDetail}
      />

      {/* 配合比定義 削除確認 */}
      <Dialog open={!!confirmDeleteDef} onClose={() => setConfirmDeleteDef(null)} fullWidth maxWidth="sm">
        <DialogTitle>配合比の削除確認</DialogTitle>
        <DialogContent>
          {confirmDeleteDef && (() => {
            const target = confirmDeleteDef;
            const relatedDetails = allDetails.filter((d) => d.mix_ratio_definition_id === target.id);
            const recipeLabel = mixRatioDisplayName(db, target);
            return (
              <Stack gap={1}>
                <Typography>
                  配合比「<b>#{target.id} {matName(target.material_id)} / {recipeLabel}</b>」を論理削除します。
                </Typography>
                {relatedDetails.length > 0 && (
                  <Typography color="warning.main">
                    紐づく配合比明細 <b>{relatedDetails.length} 件</b> もまとめて論理削除されます。
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  論理削除のためデータは内部に残置されます（ID は再利用されません）。
                </Typography>
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteDef(null)}>キャンセル</Button>
          <Button color="error" variant="contained" disabled={!isOnline} onClick={() => confirmDeleteDef && deleteDefinition(confirmDeleteDef)}>削除</Button>
        </DialogActions>
      </Dialog>

      {/* 配合比明細 削除確認 */}
      <Dialog open={!!confirmDeleteDetail} onClose={() => setConfirmDeleteDetail(null)}>
        <DialogTitle>配合比明細の削除確認</DialogTitle>
        <DialogContent>
          {confirmDeleteDetail && (
            <Typography>
              明細「<b>{detailLabel(confirmDeleteDetail.material_detail_id)}</b>」を論理削除します。よろしいですか？
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteDetail(null)}>キャンセル</Button>
          <Button color="error" variant="contained" disabled={!isOnline} onClick={() => confirmDeleteDetail && deleteDetail(confirmDeleteDetail)}>削除</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
